import { Button, Divider, Form, InputNumber, Switch, Typography, message } from 'antd'
import { useEffect } from 'react'
import { platformUpdateEntitlements } from '../api'
import type { EntitlementsMap } from '../types'

const FEATURE_LABELS: Record<string, string> = {
  'feature.custom_domain': 'Custom Domain',
  'feature.deepzoom': 'DeepZoom',
  'feature.page_builder': 'Page Builder',
  'feature.ai_image': 'AI Image',
  'feature.browser_automation': 'Browser Automation',
  'feature.image_processing': 'Image Processing',
  // VISTOERA_SAAS_TENANT_FEATURE_ENTITLEMENT_MATRIX_V1 — same keys as the
  // "租户套餐" Tenant Feature Entitlement Matrix page; kept here too so a
  // single-tenant view still shows every gate this Workspace has.
  'feature.ai_chat': 'AI Chat',
  'feature.flat_image_optimization': 'Flat Image Optimization',
  'feature.video_generation': 'Video Generation',
  'feature.video_continuation': 'Video Continuation',
  'feature.website': 'Website Content',
  'feature.site_publish': 'Site Publish',
}

const QUOTA_LABELS: Record<string, string> = {
  'site.max_count': 'Site Limit',
  'storage.max_bytes': 'Storage Limit (bytes)',
}

/** Spec §24 — real writes to the database on Save, never a fake toggle.
 * Used only from the Platform Admin's Tenant detail page; there is
 * deliberately no equivalent write form anywhere in the Workspace/Tenant
 * scope UI (spec §1: Billing/Entitlement changes are Platform-scope-only
 * this phase). */
export default function EntitlementsForm({
  workspaceId,
  entitlements,
  onSaved,
}: {
  workspaceId: string
  entitlements: EntitlementsMap
  onSaved: (next: EntitlementsMap) => void
}) {
  const [form] = Form.useForm()

  useEffect(() => {
    form.setFieldsValue(entitlements)
  }, [entitlements, form])

  const onFinish = async (values: EntitlementsMap) => {
    try {
      const { data } = await platformUpdateEntitlements(workspaceId, values)
      onSaved(data.entitlements)
      message.success('Entitlements saved')
    } catch (err) {
      message.error((err as Error).message)
    }
  }

  return (
    <Form form={form} layout="horizontal" onFinish={onFinish} initialValues={entitlements} style={{ maxWidth: 480 }}>
      <Typography.Title level={5}>Features</Typography.Title>
      {Object.entries(FEATURE_LABELS).map(([key, label]) => (
        <Form.Item key={key} name={key} label={label} valuePropName="checked">
          <Switch data-testid={`entitlement-switch-${key}`} />
        </Form.Item>
      ))}
      <Divider />
      <Typography.Title level={5}>Quotas</Typography.Title>
      {Object.entries(QUOTA_LABELS).map(([key, label]) => (
        <Form.Item key={key} name={key} label={label} rules={[{ required: true, type: 'number', min: 0 }]}>
          <InputNumber data-testid={`entitlement-quota-${key}`} style={{ width: '100%' }} min={0} />
        </Form.Item>
      ))}
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </Form.Item>
    </Form>
  )
}
